import { Alert, Table } from 'antd'
import { ColumnsType } from 'antd/es/table/interface'
import { Breakpoint } from 'antd/lib/_util/responsiveObserver'
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
} from 'react'

import { EpoTableItem } from '../../../common/epoTypes'
import {
  epoDetailLoading,
  epoDetailLocale,
  epoDetailSummary,
  podilRenderer,
  podilSorter,
  rizeniFilter,
  rizeniRenderer,
  rizeniSorter,
  rowKey,
} from '../../utils/cuzkEpoDetailTableHelpers'
import {
  nestedStringSorter,
  stringSorter,
} from '../../utils/generalTableHelpers'
import { sparsePagination } from '../../utils/layoutConst'
import { useColumnFilters } from '../../utils/useColumnFilters'
import { useColumnOptionFilter } from '../../utils/useColumnOptionFilter'
import { useFetchEpoJsonData } from '../../utils/useFetchEpoJsonData'
import { useUniqueValues } from '../../utils/useUniqueValues'
import { CuzkEpoDetailStopFetching } from '../CuzkEpoDetailStopFetching/CuzkEpoDetailStopFetching'
import { LvDetailDrawerLink } from '../LvDetailDrawerLink/LvDetailDrawerLink'
import { MapTooltip } from '../MapTooltip/MapTooltip'

export interface CuzkEpoDetailTableProps {
  epoId: string
  lvCount?: number | null
  isHugeSubject: boolean
}

const tableId = 'cuzkEpoDetail-table'
const scrollX = { x: true as const }

export const CuzkEpoDetailTable: FunctionComponent<CuzkEpoDetailTableProps> =
  props => {
    const { fetching, error, data, fetchData } = useFetchEpoJsonData(
      props.epoId
    )

    const { searchFilter, dualSearchFilter, customSearchFilter } =
      useColumnFilters<EpoTableItem>()

    const typravNazevOptions = useUniqueValues(data || [], 'TYPRAV_NAZEV')
    const { getColumnOptionFilter } =
      useColumnOptionFilter<EpoTableItem>(typravNazevOptions)

    useEffect(() => {
      if (props.isHugeSubject === false) fetchData()
    }, [fetchData, props.isHugeSubject])

    const columns = useMemo<ColumnsType<EpoTableItem>>(
      () => [
        {
          title: 'ID',
          dataIndex: 'id',
          responsive: ['' as Breakpoint],
        },
        {
          title: 'Typ oprávnění',
          dataIndex: 'TYPRAV_NAZEV',
          sorter: stringSorter('TYPRAV_NAZEV'),
          ...getColumnOptionFilter('TYPRAV_NAZEV'),
        },
        {
          title: 'Okres',
          dataIndex: 'OKRES',
          sorter: stringSorter('OKRES'),
          ...searchFilter('OKRES', 'Hledat okres'),
        },
        {
          title: 'Obec',
          dataIndex: 'OBEC',
          sorter: stringSorter('OBEC'),
          ...searchFilter('OBEC', 'Hledat obec'),
        },
        {
          title: 'Katastrální území',
          dataIndex: 'KATUZE',
          sorter: nestedStringSorter('KATUZE', 'nazev'),
          render: (katuze, record) => (
            <MapTooltip query={record.OBEC + ' ' + katuze?.nazev}>
              {katuze?.nazev}
            </MapTooltip>
          ),
        },
        {
          title: 'LV',
          dataIndex: 'LV',
          className: '!text-right',
          ...dualSearchFilter('LV', 'JPV_LV', 'Hledat LV'),
          render: (LV, record) => (
            <LvDetailDrawerLink
              lvCode={record.KATUZE?.kod || ''}
              lvNumber={LV || record.JPV_LV}
            />
          ),
        },
        {
          title: 'Podíl',
          dataIndex: 'PODIL',
          className: '!text-right',
          sorter: podilSorter,
          render: podilRenderer,
        },
        {
          title: 'Řízení',
          dataIndex: 'RIZENI',
          className: '!text-right',
          sorter: rizeniSorter,
          ...customSearchFilter(rizeniFilter, 'Vyhledat v řízení'),
          render: rizeniRenderer,
        },
      ],
      [
        getColumnOptionFilter,
        customSearchFilter,
        dualSearchFilter,
        searchFilter,
      ]
    )

    const summary = useCallback(
      () =>
        props.lvCount ? epoDetailSummary(data?.length, props.epoId) : undefined,
      [data?.length, props.epoId, props.lvCount]
    )

    if (error)
      return (
        <Alert
          message="Chyba dat"
          description="Nepodařilo se načíst data z ČÚZK."
          type="error"
          className="my-8"
          showIcon
        />
      )

    if (props.isHugeSubject && !fetching && !data?.length)
      return (
        <CuzkEpoDetailStopFetching
          lvCount={props.lvCount}
          epoId={props.epoId}
          setStopFetching={fetchData}
        />
      )

    if (fetching) return epoDetailLoading

    if (!data) return null

    return (
      <Table
        id={tableId}
        rowKey={rowKey}
        dataSource={data}
        size="small"
        scroll={scrollX}
        columns={columns}
        loading={fetching}
        locale={!fetching && epoDetailLocale}
        pagination={sparsePagination}
        summary={summary}
      />
    )
  }
